import React from 'react';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { ThemeProvider } from '@material-ui/core/styles';
import { END } from 'redux-saga';
import track from 'react-tracking';
import { SnackbarProvider } from 'notistack';
import { DefaultSeo, SocialProfileJsonLd, LogoJsonLd } from 'next-seo';
import { useSelector } from 'react-redux';
import { find, get, isEmpty } from 'lodash';
import { createInstance, OptimizelyProvider, withOptimizely, setLogger } from '@optimizely/react-sdk';
import { v4 as uuidv4 } from 'uuid';
import { useRouter, Router } from 'next/router';
import { ReactQueryDevtools } from 'react-query/devtools';
import NProgress from 'nprogress';
import 'slick-carousel/slick/slick-theme.css';
import 'slick-carousel/slick/slick.css';
import '../styles/styles.scss';
import 'react-day-picker/lib/style.css';
import { QueryClientProvider } from 'react-query';
import { Hydrate } from 'react-query/hydration';
import dayjs from 'dayjs';
import localeData from 'dayjs/plugin/localeData';
import { appWithTranslation, i18n } from '../src/i18n';
import { getCountriesAction, setIsLoggedInAction, tokenLoginAction, getUserConstants } from '../store/actions';
import { getActiveProfileIdFromCookies, getToken } from '../utils/auth';
import { getBugsnagPlugin, isBugsnagInitialized } from '../utils/bugsnagClientWrapper';
import { setIntervalForPing } from '../utils/common';
import { getCookie } from '../utils/cookie';
import { USER_TOKEN_COOKIE_KEY, ACTIVE_PROFILE_ID } from '../constants/cookieConstants';
import { getCommonUserData, setUserData, getActiveProfileAction } from '../containers/App/actions';
import { setServerContext } from '../utils/serverContext';
import usePermissions from '../utils/permissions';
import CookieBanner from '../components/CookieBanner';
import { selectListData, selectIsLoggedInState } from '../store/selectors';
import { selectAppDomain, selectUserData } from '../containers/App/selectors';
import { initialState } from '../containers/App/reducer';
import { trackUserAction, resetUserOnLogout } from '../utils/tracking';
import { trackUserActivityEvents } from '../utils/userActivityEvents';
import { queryClient, setLanguage as setLanguageForReactQuery } from '../utils/queriesUtil';
import PageScrollProvider from '../utils/hooks/usePageScroll';
import theme from '../src/theme';
import { wrapper } from '../store/store';
import 'nprogress/nprogress.css';
dayjs.extend(localeData);

// dynamic imports
const CssBaseline = dynamic(() => import('@material-ui/core/CssBaseline'));
const Page = dynamic(() => import('../components/Page'));
const Error = dynamic(() => import('./_error'));
const Notifier = dynamic(() => import('../components/Notifier'));
const OBAppContainer = dynamic(() => import('../containers/App'));
const SEO = dynamic(() => import('../utils/seo'));
const baseURL = process.env.FRONTBASE_URL;

const optimizely = createInstance({
  sdkKey: process.env.OPTIMIZELY_KEY,
});

Router.events.on('routeChangeStart', () => NProgress.start());
Router.events.on('routeChangeComplete', () => NProgress.done());
Router.events.on('routeChangeError', () => NProgress.done());

const renderAppContainer = (Component, pageProps, userDetails) => (
  <PageScrollProvider>
    <Page>
      <Head>
        <meta name="viewport" content="width=device-width, initial-scale=1" />
      </Head>
      <DefaultSeo {...SEO} />
      <SocialProfileJsonLd
        id="https://www.operabase.com/en"
        type="Organization"
        name="Operabase"
        url="https://www.operabase.com/en"
        address={{
          addressLocality: 'London, United Kingdom',
          streetAddress: 'Aldgate Tower, 2 Leman Street',
          postalCode: 'E1 8QN',
          addressCountry: 'UK',
        }}
        sameAs={[
          'https://www.facebook.com/operabase/',
          'https://twitter.com/BaseOpera',
          'https://www.youtube.com/user/operabase',
          'https://en.wikipedia.org/wiki/Operabase',
          'https://www.linkedin.com/company/operabase-global',
          'https://www.instagram.com/operabaseglobal/',
        ]}
      />
      <LogoJsonLd
        logo={`${baseURL.replace(/\/$/, '')}/images/operabase-logo-large.png`}
        url="https://www.operabase.com/en"
      />
      <OptimizelyProvider
        optimizely={optimizely}
        user={{
          id: !isEmpty(userDetails) ? userDetails.id.toString() : uuidv4(),
        }}
      >
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <SnackbarProvider maxSnack={3}>
            <Notifier />
            <QueryClientProvider client={queryClient}>
              <Hydrate state={pageProps.dehydratedState}>
                <OBAppContainer Component={Component} pageProps={pageProps} />
                <ReactQueryDevtools initialIsOpen={false} />
              </Hydrate>
            </QueryClientProvider>
          </SnackbarProvider>
        </ThemeProvider>
        <CookieBanner />
      </OptimizelyProvider>
    </Page>
  </PageScrollProvider>
);

const addDayjsLocale = locale => {
  try {
    // eslint-disable-next-line global-require
    dayjs.locale(require(`dayjs/locale/${locale}.js`));
    // eslint-disable-next-line no-empty
  } catch (ex) {}
};

let router = '';
function OBApp({ Component, pageProps }) {
  const { isTranslator } = usePermissions();
  router = useRouter();
  const settings = useSelector(selectListData(selectAppDomain, initialState.settings.listName));
  const userDetails = useSelector(selectUserData(selectAppDomain));
  const contextEditor = get(find(settings, { identifier: 'context_editor' }), 'value') === 'true';
  const isLoggedIn = useSelector(selectIsLoggedInState());
  React.useEffect(() => {
    trackUserActivityEvents();
    // reset user details in google analytics when user is not logged in
    if (!isLoggedIn) {
      resetUserOnLogout();
    }
    // only on client
    const cookieToken = getCookie(USER_TOKEN_COOKIE_KEY);
    if (cookieToken) {
      setIntervalForPing();
    }
    // Remove the server-side injected CSS.
    const jssStyles = document.querySelector('#jss-server-side');
    if (jssStyles) {
      jssStyles.parentElement.removeChild(jssStyles);
    }
  }, []);
  setLogger(null);

  React.useEffect(() => {
    const projectId = process.env.LOKALISE_PROJECT_ID;
    if (isTranslator && contextEditor && projectId) {
      window.LOKALISE_CONFIG = {
        projectId,
        locale: 'en',
        plainKey: true,
      };

      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.src = ['https://app.lokalise.com/live-js/script.min.js?', new Date().getTime()].join('');
      script.async = true;
      document.head.append(script);
    }
  }, [isTranslator, contextEditor]);

  React.useEffect(() => {
    if (isTranslator) {
      document.dispatchEvent(new Event('lokalise-update-elements'));
    }
  }, [contextEditor]);

  // Cannot use effect as should wait for locale before render
  const locale = i18n?.language?.toLowerCase();
  if (locale && locale !== dayjs.locale() && locale !== 'keys') {
    addDayjsLocale(locale);
  }

  if (isBugsnagInitialized()) {
    const ErrorBoundary = getBugsnagPlugin();
    return (
      <ErrorBoundary FallbackComponent={Error}>{renderAppContainer(Component, pageProps, userDetails)}</ErrorBoundary>
    );
  }
  return <div>{renderAppContainer(Component, pageProps, userDetails)}</div>;
}

OBApp.getInitialProps = async ({ Component, ctx }) => {
  // set the logged-in state
  const userToken = getToken(ctx);
  const { store } = ctx;
  const state = store.getState();

  const locale = i18n?.language?.toLowerCase() || ctx?.req?.language;
  if (locale && locale !== dayjs.locale() && locale !== 'keys') {
    addDayjsLocale(locale);
  }

  // set queryLanguageKey
  setLanguageForReactQuery(locale);

  const { userData, activeProfileData } = state.App;

  const { isLoggedIn, countries, countriesLoading, userConstants } = state.global;
  const isBrowser = typeof window;
  // check if user logged in and compare with previous state to getuserdetails
  const getUserDetailsByToken = () => {
    const profileId = getCookie(ACTIVE_PROFILE_ID);
    const parsedProfileId = profileId ? parseInt(profileId, 10) : null;
    if (!isLoggedIn) {
      setIntervalForPing();
      store.dispatch(tokenLoginAction({ token: userToken }));
    } else if (parsedProfileId && activeProfileData?.id !== parsedProfileId && userData?.id) {
      store.dispatch(getActiveProfileAction({ activeProfileId: profileId, userId: userData.id }));
    }
  };

  const getUserDetailsInBrowser = () => {
    if (userToken) {
      getUserDetailsByToken();
    } else {
      store.dispatch(setIsLoggedInAction(false));
    }
  };

  const getUserDetailsInServer = userDetails => {
    if (userDetails) {
      if (userDetails.user) {
        store.dispatch(setUserData({ user: userDetails.user }));
      }
      store.dispatch(setIsLoggedInAction(true));
      const activeProfileId = getActiveProfileIdFromCookies(ctx);
      store.dispatch(getCommonUserData({ ...userDetails, activeProfileId }));
    } else {
      store.dispatch(setIsLoggedInAction(false));
    }
  };
  // set cookies to the outgoing api calls; if not running in browser
  if (isBrowser === 'undefined') {
    setServerContext(ctx);
    const userDetails = ctx.req.X_User_Details;
    getUserDetailsInServer(userDetails);
  }

  if (isBrowser === 'object') {
    getUserDetailsInBrowser();
  }

  if (!userConstants.length) {
    store.dispatch(getUserConstants());
  }

  const pageProps = {
    ...(Component.getInitialProps ? await Component.getInitialProps(ctx) : {}),
  };

  if (ctx.req) {
    ctx.store.dispatch(END);
    await ctx.store.sagaTask.toPromise();
  }

  if (countries.length === 0 && !countriesLoading) {
    store.dispatch(getCountriesAction());
  }

  return { pageProps };
};

export function reportWebVitals(metric) {
  if (process.env.NODE_ENV === 'development') {
    console.log(metric);
  }
}

const TrackedApp = track(
  { app: 'Frontbase' },
  // top-level options
  {
    // custom dispatch in addition to pushing segment
    dispatch: payload => {
      const data = trackUserAction(payload, router);
      if (window && window.analytics) {
        window.analytics.track(data.name, data);
      }
    },
  },
)(OBApp);

export default withOptimizely(wrapper.withRedux(appWithTranslation(TrackedApp)));
